import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, MessageSquare, MessageCircle } from "lucide-react";

const emptyForm = {
  keyword: "",
  response_text: "",
  response_link: "",
  match_type: "contains",
  trigger_type: "dm",
  comment_public_reply: "",
  is_active: true,
};

const TRIGGER_LABEL = {
  dm: "DM",
  comment: "Comment",
  both: "DM + Comment",
};

export default function Keywords() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/keywords"); setItems(data); }
    catch (err) { console.error("Failed to load keywords:", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (k) => {
    setEditing(k);
    setForm({
      keyword: k.keyword,
      response_text: k.response_text,
      response_link: k.response_link || "",
      match_type: k.match_type,
      trigger_type: k.trigger_type || "dm",
      comment_public_reply: k.comment_public_reply || "",
      is_active: k.is_active,
    });
    setOpen(true);
  };
  const close = () => { setOpen(false); setEditing(null); setForm(emptyForm); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/keywords/${editing.id}`, form);
        toast.success("Flow updated");
      } else {
        await api.post("/keywords", form);
        toast.success("Flow created");
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const toggleActive = async (k) => {
    try {
      await api.patch(`/keywords/${k.id}`, { is_active: !k.is_active });
      load();
    } catch (err) { console.error("Toggle failed:", err); toast.error("Update failed"); }
  };

  const del = async (k) => {
    if (!confirm(`Delete "${k.keyword}"?`)) return;
    try {
      await api.delete(`/keywords/${k.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { console.error("Delete failed:", err); toast.error("Delete failed"); }
  };

  const showCommentFields = form.trigger_type === "comment" || form.trigger_type === "both";

  return (
    <div className="p-8 md:p-12 max-w-[1400px]">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
        <div>
          <div className="overline mb-3">— Keyword Manager</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter leading-none" data-testid="keywords-title">Flows.</h1>
          <p className="mt-3 text-white/60">Pair a keyword with an automated response. Trigger from DMs, comments, or both.</p>
        </div>
        <button onClick={openCreate} className="btn-primary" data-testid="keywords-new-btn">
          <Plus className="w-4 h-4" /> New flow
        </button>
      </div>

      {items.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="keywords-empty">
          <div className="mx-auto w-56 h-56 md:w-72 md:h-72 mb-8 rounded-sm overflow-hidden bg-white/5 backdrop-blur-xl border border-white/10 p-3">
            <img src="/empty-state-desk.jpg" alt="Waiting for your first keyword" className="w-full h-full object-cover rounded-sm brightness-[0.8] contrast-[1.15] saturate-[0.85]" />
          </div>
          <div className="font-heading font-black text-3xl tracking-tighter mb-3">Waiting for your first keyword.</div>
          <p className="text-white/60 mb-8 max-w-md mx-auto">Create a flow to start turning DMs into conversions.</p>
          <button onClick={openCreate} className="btn-primary inline-flex" data-testid="keywords-empty-cta">
            <Plus className="w-4 h-4" /> New flow
          </button>
        </div>
      ) : (
        <div className="surface-card overflow-hidden" data-testid="keywords-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 bg-bone">
                <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Keyword</th>
                <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Trigger</th>
                <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Response</th>
                <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Match</th>
                <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Triggers</th>
                <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((k) => {
                const tt = k.trigger_type || "dm";
                return (
                  <tr key={k.id} className="border-b border-white/5 hover:bg-surface/5 transition-colors" data-testid={`kw-row-${k.id}`}>
                    <td className="py-5 px-6 font-mono font-semibold text-klein whitespace-nowrap">{k.keyword}</td>
                    <td className="py-5 px-6 text-xs">
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-surface/5 font-bold uppercase tracking-wider">
                        {tt === "comment" ? <MessageCircle className="w-3 h-3" /> : tt === "both" ? <><MessageSquare className="w-3 h-3" /><MessageCircle className="w-3 h-3" /></> : <MessageSquare className="w-3 h-3" />}
                        {TRIGGER_LABEL[tt]}
                      </span>
                    </td>
                    <td className="py-5 px-6 text-sm text-white/80 max-w-md truncate">{k.response_text}</td>
                    <td className="py-5 px-6 text-sm text-white/70">{k.match_type}</td>
                    <td className="py-5 px-6 font-semibold">{k.trigger_count || 0}</td>
                    <td className="py-5 px-6">
                      <button onClick={() => toggleActive(k)} className={`px-2 py-1 text-[10px] uppercase tracking-wider font-bold ${k.is_active ? "bg-klein/10 text-klein" : "bg-surface/10 text-white/60"}`} data-testid={`kw-toggle-${k.id}`}>
                        {k.is_active ? "Live" : "Paused"}
                      </button>
                    </td>
                    <td className="py-5 px-6 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(k)} className="p-2 hover:bg-surface/5" data-testid={`kw-edit-${k.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(k)} className="p-2 hover:bg-surface/5 text-rose-400" data-testid={`kw-delete-${k.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-obsidian/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="keyword-modal">
          <form onSubmit={save} onClick={(e)=>e.stopPropagation()} className="bg-bone border border-white/10 p-8 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="overline mb-1">— {editing ? "Edit" : "New"} flow</div>
                <h2 className="font-heading font-black text-2xl tracking-tighter">{editing ? "Edit flow" : "Create flow"}</h2>
              </div>
              <button type="button" onClick={close} className="p-2 hover:bg-surface/5" data-testid="modal-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="label-overline">Trigger</label>
                <div className="grid grid-cols-3 gap-2" data-testid="form-trigger-type">
                  {[
                    { id: "dm", label: "DM", icon: MessageSquare, hint: "Reply to DMs" },
                    { id: "comment", label: "Comment", icon: MessageCircle, hint: "Reply to post comments" },
                    { id: "both", label: "Both", icon: null, hint: "DMs + comments" },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    const active = form.trigger_type === opt.id;
                    return (
                      <button
                        type="button"
                        key={opt.id}
                        onClick={() => setForm({ ...form, trigger_type: opt.id })}
                        className={`p-3 border text-left transition-colors ${active ? "border-white bg-obsidian text-white" : "border-white/15 hover:border-white/40"}`}
                        data-testid={`form-trigger-${opt.id}`}
                      >
                        <div className="flex items-center gap-1.5 font-bold text-xs uppercase tracking-wider">
                          {Icon && <Icon className="w-3.5 h-3.5" />}
                          {opt.label}
                        </div>
                        <div className={`text-[11px] mt-1 ${active ? "text-white/70" : "text-white/50"}`}>{opt.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="label-overline">Keyword</label>
                <input value={form.keyword} onChange={(e)=>setForm({...form, keyword: e.target.value})} required className="input-field" placeholder="e.g. PAISLEY" data-testid="form-keyword" />
              </div>
              <div>
                <label className="label-overline">Match type</label>
                <select value={form.match_type} onChange={(e)=>setForm({...form, match_type: e.target.value})} className="input-field" data-testid="form-match-type">
                  <option value="contains">Contains (whole word)</option>
                  <option value="exact">Exact match</option>
                  <option value="starts_with">Starts with</option>
                </select>
              </div>
              <div>
                <label className="label-overline">DM response text</label>
                <textarea value={form.response_text} onChange={(e)=>setForm({...form, response_text: e.target.value})} required rows={4} className="input-field" placeholder="Here's your link!" data-testid="form-response" />
              </div>
              <div>
                <label className="label-overline">Link (optional)</label>
                <input value={form.response_link} onChange={(e)=>setForm({...form, response_link: e.target.value})} className="input-field" placeholder="https://…" data-testid="form-link" />
              </div>
              {showCommentFields && (
                <div data-testid="form-comment-section">
                  <label className="label-overline">Public comment reply (optional)</label>
                  <input
                    value={form.comment_public_reply}
                    onChange={(e)=>setForm({...form, comment_public_reply: e.target.value})}
                    className="input-field"
                    placeholder="Just sent you a DM 💌"
                    data-testid="form-comment-public-reply"
                  />
                  <p className="text-[11px] text-white/50 mt-1.5">Leave empty to DM silently (no public reply).</p>
                </div>
              )}
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={(e)=>setForm({...form, is_active: e.target.checked})} className="w-4 h-4 accent-klein" data-testid="form-active" />
                <span className="text-sm font-semibold">Active (live immediately)</span>
              </label>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button type="button" onClick={close} className="btn-ghost" data-testid="modal-cancel">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="modal-save">{saving ? "Saving…" : (editing ? "Save changes" : "Create flow")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
